import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Hierarchy roles (0 = highest, 9 = lowest)
export const ROLE_HIERARCHY: Record<string, number> = {
  owner: 0,
  school_manager: 1,
  branch_manager: 2,
  finance_manager: 3,
  accountant: 4,
  teacher: 5,
  librarian: 6,
  staff: 7,
  parent: 8,
  student: 9,
  // Legacy aliases
  superadmin: 0,
  admin: 1,
  principal: 2,
  manager: 1,      // old manager = school_manager
};

export const ROLE_LABELS: Record<string, string> = {
  owner: 'مالك المدرسة',
  school_manager: 'مدير المدرسة',
  branch_manager: 'مدير الفرع',
  finance_manager: 'مدير القسم المالي',
  accountant: 'محاسب',
  teacher: 'معلم',
  librarian: 'أمين المكتبة',
  staff: 'موظف',
  parent: 'ولي الأمر',
  student: 'طالب',
  superadmin: 'Super Admin',
  admin: 'Admin',
  principal: 'Principal',
};

export type UserRole =
  | 'owner'
  | 'school_manager'
  | 'branch_manager'
  | 'finance_manager'
  | 'accountant'
  | 'teacher'
  | 'librarian'
  | 'staff'
  | 'parent'
  | 'student'
  | 'superadmin'
  | 'admin'
  | 'principal'
  | 'manager';

// Re-export User type from central types to ensure consistency
import type { User as AuthUser } from '../types/auth';

// Extend the auth User with our hierarchy-specific fields
export interface User extends AuthUser {
  role_level: number;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  updateUser: (partial: Partial<User>) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setTokens: (access: string, refresh: string) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  // Hierarchy helpers
  canManageRole: (targetRole: UserRole) => boolean;
  isRoleOrAbove: (minRole: UserRole) => boolean;
  // Compatibility helpers
  getUserFullName: () => string;
  getUserRole: () => UserRole | undefined;
  getUserRoleLevel: () => number;
  hasRole: (minLevel: number) => boolean;
  hasPermission: (permission: string) => boolean;
}

// Map mock email to role
function getRoleFromEmail(email: string): UserRole {
  if (email.includes('owner')) return 'owner';
  if (email.includes('school_manager') || email.includes('schoolmanager')) return 'school_manager';
  if (email.includes('branch_manager') || email.includes('branchmanager')) return 'branch_manager';
  if (email.includes('finance_manager') || email.includes('financemanager')) return 'finance_manager';
  if (email.includes('accountant')) return 'accountant';
  if (email.includes('teacher')) return 'teacher';
  if (email.includes('parent')) return 'parent';
  return 'staff';
}

function getNameFromRole(role: UserRole): string {
  const names: Record<string, string> = {
    owner: 'مالك المدرسة',
    school_manager: 'مدير المدرسة',
    branch_manager: 'مدير الفرع',
    finance_manager: 'مدير القسم المالي',
    accountant: 'محاسب',
    teacher: 'معلم',
    librarian: 'أمين المكتبة',
    staff: 'موظف',
    parent: 'ولي الأمر',
    student: 'طالب',
    superadmin: 'Super Admin',
    admin: 'Admin',
    principal: 'Principal',
  };
  return names[role] || 'مستخدم';
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      updateUser: (partial: Partial<User>) => {
        const current = get().user;
        if (current) {
          set({ user: { ...current, ...partial } });
        }
      },
      login: async (emailOrData: string | { user: User; access: string; refresh: string; token_type?: string }, _password?: string) => {
        set({ isLoading: true, error: null });
        try {
          // Handle object form (from AuthContext)
          if (typeof emailOrData === 'object') {
            const { user, access, refresh } = emailOrData;
            set({
              user: { ...user, role_level: user.role_level ?? ROLE_HIERARCHY[user.role] ?? 99 },
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              accessToken: access,
              refreshToken: refresh,
            });
            return;
          }
          // Handle string form (from Login page)
          const email = emailOrData;
          // Simulate API call
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const role = getRoleFromEmail(email);
          const mockUser: User = {
            id: '1',
            name: getNameFromRole(role),
            email,
            role,
            role_level: ROLE_HIERARCHY[role] ?? 99,
          };
          set({
            user: mockUser,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            accessToken: 'mock-access-token',
            refreshToken: 'mock-refresh-token',
          });
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Login failed',
          });
          throw err;
        }
      },
      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
          error: null,
        });
      },
      setLoading: (loading) => set({ isLoading: loading }),
      setInitialized: (initialized) => set({ isInitialized: initialized }),
      setTokens: (access, refresh) => {
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        set({ accessToken: access, refreshToken: refresh });
      },
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      canManageRole: (targetRole: UserRole) => {
        const user = get().user;
        if (!user) return false;
        const userLevel = user.role_level ?? ROLE_HIERARCHY[user.role] ?? 99;
        const targetLevel = ROLE_HIERARCHY[targetRole] ?? 99;
        return userLevel < targetLevel;
      },
      isRoleOrAbove: (minRole: UserRole) => {
        const user = get().user;
        if (!user) return false;
        const userLevel = user.role_level ?? ROLE_HIERARCHY[user.role] ?? 99;
        return userLevel <= (ROLE_HIERARCHY[minRole] ?? 99);
      },
      // Compatibility helpers for useAuth.ts
      getUserFullName: () => {
        const user = get().user;
        if (!user) return '';
        return user.name ?? user.full_name ?? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ?? '';
      },
      getUserRole: () => {
        const user = get().user;
        return user?.role as UserRole | undefined;
      },
      getUserRoleLevel: () => {
        const user = get().user;
        return user?.role_level ?? 99;
      },
      hasRole: (minLevel: number) => {
        const user = get().user;
        if (!user) return false;
        return user.role_level <= minLevel;
      },
      hasPermission: (_permission: string) => {
        // TODO: implement permission check
        return true;
      },
    }),
    {
      name: 'abudhar-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isInitialized: state.isInitialized,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
